import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services';
import { utils } from '../../utils';
import FilterPanel from '../../components/Common/FilterPanel';
import ResultsCounter from '../../components/Common/ResultsCounter';
import LoadMoreButton from '../../components/Common/LoadMoreButton';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';

interface PreventiveMaintenanceItem {
  id: number;
  veiculo: string;
  descricao: string;
  data_agendamento: string;
  data_execucao?: string;
  custo_estimado: number;
  custo_real?: number;
  fornecedor: string;
  status: string;
  prioridade: string;
  quilometragem_programada: number;
  quilometragem_atual: number;
  periodicidade: string;
  observacoes?: string;
}

const PreventiveMaintenance: React.FC = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('');
  const [vehicleFilter, setVehicleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Hook de infinite scroll
  const {
    items: maintenances,
    isLoading,
    totalItems,
    displayedItems,
    hasMore,
    loadMore
  } = useInfiniteScroll<PreventiveMaintenanceItem>({
    fetchFunction: async (page, limit, filters) => {
      try {
        const response = await apiService.getPreventiveMaintenances({
          page,
          limit
        });

        if (response.success && response.data) {
          const apiMaintenances = Array.isArray(response.data.maintenances) ? response.data.maintenances : [];

          const mappedMaintenances: PreventiveMaintenanceItem[] = apiMaintenances.map((item: any) => ({
            id: item.id,
            veiculo: item.veiculo || item.veiculo_placa || 'N/A',
            descricao: item.descricao || '',
            data_agendamento: item.data_agendamento,
            data_execucao: item.data_execucao,
            custo_estimado: item.custo_estimado || 0,
            custo_real: item.custo_real,
            fornecedor: item.fornecedor || item.fornecedor_nome || 'N/A',
            status: item.status || 'Agendada',
            prioridade: item.prioridade || 'Média',
            quilometragem_programada: item.quilometragem_programada || 0,
            quilometragem_atual: item.quilometragem_atual || 0,
            periodicidade: item.periodicidade || '',
            observacoes: item.observacoes
          }));

          return {
            success: true,
            data: {
              items: mappedMaintenances,
              pagination: {
                total: response.data.pagination?.total || mappedMaintenances.length
              }
            }
          };
        }

        throw new Error('Resposta inválida da API');
      } catch (error) {
        console.error('Erro ao carregar manutenções preventivas:', error);
        return {
          success: false,
          data: [],
          message: 'Erro ao carregar manutenções preventivas'
        };
      }
    },
    itemsPerPage: 10,
    filters: {},
    onError: (error) => {
      utils.showNotification(error, { type: 'error' });
    }
  });

  const getStatusClass = (status: string): string => {
    const statusMap: Record<string, string> = {
      'Concluída': 'status-success',
      'Em Andamento': 'status-warning',
      'Agendada': 'status-info',
      'Cancelada': 'status-danger',
      'Atrasada': 'status-danger'
    };
    return statusMap[status] || 'status-secondary';
  };

  const getPriorityClass = (prioridade: string): string => {
    const priorityMap: Record<string, string> = {
      'Urgente': 'status-danger',
      'Alta': 'status-warning',
      'Média': 'status-info',
      'Baixa': 'status-success'
    };
    return priorityMap[prioridade] || 'status-secondary';
  };

  const formatDateTime = (dateString: string): string => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatNumber = (value: number): string => {
    return new Intl.NumberFormat('pt-BR').format(value);
  };

  const filteredMaintenances = maintenances.filter(maintenance => {
    const matchesSearch = maintenance.veiculo.toLowerCase().includes(filter.toLowerCase()) ||
                         maintenance.descricao.toLowerCase().includes(filter.toLowerCase()) ||
                         maintenance.fornecedor.toLowerCase().includes(filter.toLowerCase());
    const matchesVehicle = !vehicleFilter || maintenance.veiculo === vehicleFilter;
    const matchesStatus = !statusFilter || maintenance.status === statusFilter;
    const matchesPriority = !priorityFilter || maintenance.prioridade === priorityFilter;
    return matchesSearch && matchesVehicle && matchesStatus && matchesPriority;
  });

  const viewMaintenance = (id: number) => {
    navigate(`/manutencoes-preventivas/visualizar/${id}`);
  };

  const editMaintenance = (id: number) => {
    utils.showNotification(`Editando manutenção preventiva ${id}`, { type: 'info' });
  };

  const deleteMaintenance = (id: number) => {
    // eslint-disable-next-line no-restricted-globals
    if (confirm('Tem certeza que deseja excluir esta manutenção preventiva?')) {
      utils.showNotification('Manutenção preventiva excluída com sucesso!', { type: 'success' });
      window.location.reload();
    }
  };


  const getVehicles = () => {
    const vehicles = Array.from(new Set(maintenances.map(m => m.veiculo)));
    return vehicles;
  };

  const getStatuses = () => {
    const statuses = Array.from(new Set(maintenances.map(m => m.status)));
    return statuses;
  };

  const getPriorities = () => {
    const priorities = Array.from(new Set(maintenances.map(m => m.prioridade)));
    return priorities;
  };

  const getKmStatus = (programada: number, atual: number): string => {
    const diff = atual - programada;
    if (diff >= 1000) return 'status-danger'; // Atrasada
    if (diff >= 500) return 'status-warning'; // Próxima
    return 'status-success'; // No prazo
  };

  const getKmStatusText = (programada: number, atual: number): string => {
    const diff = atual - programada;
    if (diff >= 1000) return 'Atrasada';
    if (diff >= 500) return 'Próxima';
    return 'No prazo';
  };

  if (isLoading) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1>Manutenções Preventivas</h1>
          <button className="btn btn-primary" disabled>
            <i className="fas fa-plus"></i>
            Nova Manutenção Preventiva
          </button>
        </div>
        <div style={{ textAlign: 'center', padding: '100px 20px' }}>
          <div className="loading-spinner-large"></div>
          <p>Carregando manutenções preventivas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Manutenções Preventivas</h1>
        <button 
          className="btn btn-primary"
          onClick={() => navigate('/manutencoes-preventivas/novo')}
        >
          <i className="fas fa-plus"></i>
          Nova Manutenção Preventiva
        </button>
      </div>

      <div className="page-content">
        <div className="card">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2>Controle de Manutenções Preventivas</h2>
            <FilterPanel
              fields={[
                {
                  type: 'text',
                  label: 'Buscar',
                  value: filter,
                  onChange: setFilter,
                  placeholder: 'Veículo, descrição ou fornecedor...'
                },
                {
                  type: 'select',
                  label: 'Veículo',
                  value: vehicleFilter,
                  onChange: setVehicleFilter,
                  options: [
                    { value: '', label: 'Todos' },
                    ...getVehicles().map(vehicle => ({ value: vehicle, label: vehicle }))
                  ]
                },
                {
                  type: 'select',
                  label: 'Status',
                  value: statusFilter,
                  onChange: setStatusFilter,
                  options: [
                    { value: '', label: 'Todos' },
                    ...getStatuses().map(status => ({ value: status, label: status }))
                  ]
                },
                {
                  type: 'select',
                  label: 'Prioridade',
                  value: priorityFilter,
                  onChange: setPriorityFilter,
                  options: [
                    { value: '', label: 'Todas' },
                    ...getPriorities().map(priority => ({ value: priority, label: priority }))
                  ]
                }
              ]}
              onFilter={() => {}}
              onClear={() => {
                setFilter('');
                setVehicleFilter('');
                setStatusFilter('');
                setPriorityFilter('');
              }}
              showFilters={showFilters}
              onToggleFilters={() => setShowFilters(!showFilters)}
              title="Filtros de Manutenções Preventivas"
            />
          </div>

          <div className="card-body">
            {/* Contador de resultados */}
            {filteredMaintenances.length > 0 && (
              <ResultsCounter
                displayedItems={displayedItems}
                totalItems={totalItems}
                isLoading={isLoading}
                label="manutenções preventivas"
              />
            )}

            {filteredMaintenances.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-calendar-check empty-state-icon"></i>
                <h3>Nenhuma manutenção preventiva encontrada</h3>
                <p>{filter || vehicleFilter || statusFilter || priorityFilter ? 'Tente ajustar os filtros de busca.' : 'Programe a primeira manutenção preventiva do sistema.'}</p>
                <button 
                  className="btn btn-primary"
                  onClick={() => navigate('/manutencoes-preventivas/novo')}
                >
                  Programar Primeira Manutenção
                </button>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Veículo</th>
                      <th>Descrição</th>
                      <th>Periodicidade</th>
                      <th>KM Programada</th>
                      <th>KM Atual</th>
                      <th>Status KM</th>
                      <th>Data Agendamento</th>
                      <th>Data Execução</th>
                      <th>Custo Estimado</th>
                      <th>Custo Real</th>
                      <th>Fornecedor</th>
                      <th>Status</th>
                      <th>Prioridade</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMaintenances.map(maintenance => (
                      <tr key={maintenance.id}>
                        <td>
                          <strong>{maintenance.veiculo}</strong>
                        </td>
                        <td>
                          <div style={{ maxWidth: '200px' }}>
                            {maintenance.descricao}
                          </div>
                        </td>
                        <td>
                          {maintenance.periodicidade}
                        </td>
                        <td>
                          {formatNumber(maintenance.quilometragem_programada)} km
                        </td>
                        <td>
                          {formatNumber(maintenance.quilometragem_atual)} km
                        </td>
                        <td>
                          <span className={`status-badge ${getKmStatus(maintenance.quilometragem_programada, maintenance.quilometragem_atual)}`}>
                            {getKmStatusText(maintenance.quilometragem_programada, maintenance.quilometragem_atual)}
                          </span>
                        </td>
                        <td>
                          {formatDateTime(maintenance.data_agendamento)}
                        </td>
                        <td>
                          {maintenance.data_execucao ? formatDateTime(maintenance.data_execucao) : '-'}
                        </td>
                        <td>
                          {formatCurrency(maintenance.custo_estimado)}
                        </td>
                        <td>
                          {maintenance.custo_real ? (
                            <strong>{formatCurrency(maintenance.custo_real)}</strong>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td>
                          {maintenance.fornecedor}
                        </td>
                        <td>
                          <span className={`status-badge ${getStatusClass(maintenance.status)}`}>
                            {maintenance.status}
                          </span>
                        </td>
                        <td>
                          <span className={`status-badge ${getPriorityClass(maintenance.prioridade)}`}>
                            {maintenance.prioridade}
                          </span>
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button 
                              className="btn-view"
                              onClick={() => viewMaintenance(maintenance.id)}
                              title="Visualizar"
                            >
                              <i className="fas fa-eye"></i>
                            </button>
                            <button 
                              className="btn-edit"
                              onClick={() => editMaintenance(maintenance.id)}
                              title="Editar"
                            >
                              <i className="fas fa-edit"></i>
                            </button>
                            <button 
                              className="btn-delete"
                              onClick={() => deleteMaintenance(maintenance.id)}
                              title="Excluir"
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {/* Botão "Ver Mais" para carregamento manual */}
                <LoadMoreButton
                  onClick={loadMore}
                  isLoading={isLoading}
                  hasMore={hasMore}
                />
              </div>
            )}
          </div>
        </div>

        {/* Estatísticas das manutenções preventivas */}
        <div className="dashboard-grid" style={{ marginTop: '20px' }}>
          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-calendar-check"></i>
            </div>
            <div className="stat-value">
              {maintenances.length}
            </div>
            <div className="stat-label">Total de Preventivas</div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-check-circle"></i>
            </div>
            <div className="stat-value">
              {maintenances.filter(m => m.status === 'Concluída').length}
            </div>
            <div className="stat-label">Concluídas</div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-clock"></i>
            </div>
            <div className="stat-value">
              {maintenances.filter(m => m.status === 'Agendada').length}
            </div>
            <div className="stat-label">Agendadas</div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-exclamation-triangle"></i>
            </div>
            <div className="stat-value">
              {maintenances.filter(m => 
                getKmStatusText(m.quilometragem_programada, m.quilometragem_atual) === 'Atrasada'
              ).length}
            </div>
            <div className="stat-label">Atrasadas</div>
          </div>
        </div>

        {/* Distribuição por status de KM */}
        <div className="card" style={{ marginTop: '20px' }}>
          <div className="card-header">
            <h3>Status por Quilometragem</h3>
          </div>
          <div className="card-body">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
              {[
                { status: 'No prazo', count: maintenances.filter(m => getKmStatusText(m.quilometragem_programada, m.quilometragem_atual) === 'No prazo').length, color: '#04C64F' },
                { status: 'Próxima', count: maintenances.filter(m => getKmStatusText(m.quilometragem_programada, m.quilometragem_atual) === 'Próxima').length, color: '#FFA500' },
                { status: 'Atrasada', count: maintenances.filter(m => getKmStatusText(m.quilometragem_programada, m.quilometragem_atual) === 'Atrasada').length, color: '#DC3545' }
              ].map(item => {
                const percentage = maintenances.length > 0 ? Math.round((item.count / maintenances.length) * 100) : 0;
                
                return (
                  <div key={item.status} style={{ padding: '15px', background: '#f8f9fa', borderRadius: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                      <span style={{ fontWeight: '600', color: '#2c3e50' }}>{item.status}</span>
                      <span style={{ color: item.color, fontWeight: 'bold' }}>{item.count}</span>
                    </div>
                    <div style={{ background: '#e9ecef', borderRadius: '4px', height: '8px' }}>
                      <div 
                        style={{ 
                          background: `linear-gradient(135deg, ${item.color}, ${item.color}99)`, 
                          height: '100%', 
                          borderRadius: '4px', 
                          width: `${percentage}%` 
                        }}
                      ></div>
                    </div>
                    <div style={{ marginTop: '10px', fontSize: '0.8rem', color: '#7f8c8d' }}>
                      <div>Percentual: <strong>{percentage}%</strong></div>
                      <div>Veículos: <strong>{item.count}</strong></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Próximas manutenções preventivas */}
        <div className="card" style={{ marginTop: '20px' }}>
          <div className="card-header">
            <h3>Próximas Manutenções Preventivas</h3>
          </div>
          <div className="card-body">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
              {maintenances
                .filter(m => m.status === 'Agendada')
                .sort((a, b) => new Date(a.data_agendamento).getTime() - new Date(b.data_agendamento).getTime())
                .slice(0, 4)
                .map(maintenance => (
                  <div key={maintenance.id} style={{ padding: '15px', background: '#f8f9fa', borderRadius: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                      <span style={{ fontWeight: '600', color: '#2c3e50' }}>{maintenance.veiculo}</span>
                      <span className={`status-badge ${getPriorityClass(maintenance.prioridade)}`}>
                        {maintenance.prioridade}
                      </span>
                    </div>
                    <div style={{ fontSize: '0.9rem', color: '#7f8c8d', marginBottom: '10px' }}>
                      {maintenance.descricao}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#7f8c8d' }}>
                      <div>Data: <strong>{formatDateTime(maintenance.data_agendamento)}</strong></div>
                      <div>KM: <strong>{formatNumber(maintenance.quilometragem_atual)}/{formatNumber(maintenance.quilometragem_programada)} km</strong></div>
                      <div>Status KM: <span className={`status-badge ${getKmStatus(maintenance.quilometragem_programada, maintenance.quilometragem_atual)}`}>
                        {getKmStatusText(maintenance.quilometragem_programada, maintenance.quilometragem_atual)}
                      </span></div>
                      <div>Custo estimado: <strong>{formatCurrency(maintenance.custo_estimado)}</strong></div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>

        {/* Manutenções por veículo */}
        <div className="card" style={{ marginTop: '20px' }}>
          <div className="card-header">
            <h3>Manutenções Preventivas por Veículo</h3>
          </div>
          <div className="card-body">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
              {getVehicles().map(vehicle => {
                const vehicleMaintenances = maintenances.filter(m => m.veiculo === vehicle);
                const totalCost = vehicleMaintenances.reduce((total, m) => total + Number(m.custo_real || m.custo_estimado || 0), 0);
                const completed = vehicleMaintenances.filter(m => m.status === 'Concluída').length;
                const pending = vehicleMaintenances.filter(m => m.status === 'Agendada').length;
                const overdue = vehicleMaintenances.filter(m => 
                  getKmStatusText(m.quilometragem_programada, m.quilometragem_atual) === 'Atrasada'
                ).length;
                
                return (
                  <div key={vehicle} style={{ padding: '15px', background: '#f8f9fa', borderRadius: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                      <span style={{ fontWeight: '600', color: '#2c3e50' }}>{vehicle}</span>
                      <span style={{ color: '#04C64F', fontWeight: 'bold' }}>{vehicleMaintenances.length}</span>
                    </div>
                    <div style={{ fontSize: '0.9rem', color: '#7f8c8d' }}>
                      <div>Concluídas: <strong>{completed}</strong></div>
                      <div>Agendadas: <strong>{pending}</strong></div>
                      <div>Atrasadas: <strong style={{ color: overdue > 0 ? '#DC3545' : '#7f8c8d' }}>{overdue}</strong></div>
                      <div>Custo total: <strong>{formatCurrency(totalCost)}</strong></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreventiveMaintenance;
